import { Fragment, useCallback } from "react"
import classNames from "classnames"
import CustomChangeItem from "./CustomChangeItem"
import { LogChangeItem } from "@/api/LogsService"
import { Icon, IconColor, IconProps } from "@/components/shared/Icon"
import { AlertVariant, TypefaceVariant } from "@/types/alert"
import { isBoolean } from "@/utils/lodash"

const ChangesTable = ({
   changes,
   type,
   thresholdIconName,
   getAlertVariantClassName,
}: {
   changes: LogChangeItem[]
   type?: "respiratoryRate" | "heartRate"
   thresholdIconName: IconProps["name"]
   getAlertVariantClassName: (alertVariant: keyof typeof AlertVariant) => string
}) => {
   const getConfigurationTitle = useCallback((field: string): string | null => {
      switch (field) {
         case "recipients":
            return "Notification recipients"
         case "active":
            return "Notification active"
         default:
            return null
      }
   }, [])

   const maxColumns = type ? 5 : 4

   const getConfigurationIconName = useCallback(
      (field: string): IconProps["name"] => {
         switch (field) {
            case "highLimit":
               return "upperThreshold"
            case "lowLimit":
               return "lowerThreshold"
            case "highTimespan":
               return "clock"
            case "lowTimespan":
               return "clock"
            case "soundClip":
            case "soundFrequency":
               return "sound"
            case "typefaceColor":
            case "cardColor":
               return "visual"
            case "sensorTypes":
               return "alerts"
            case "recipients":
            case "active":
               return "envelope"
            default:
               return "risk"
         }
      },
      []
   )

   return (
      <table className="log-card__changes-table">
         <tbody>
            {changes.map((change, i) => {
               const { oldValue, field, newValue } = change
               const configurationTitle = getConfigurationTitle(field)
               return (
                  <Fragment key={i}>
                     {configurationTitle && (
                        <tr>
                           <td
                              className="log-card__change-subtitle"
                              colSpan={maxColumns}
                           >
                              {`${configurationTitle}:`}
                           </td>
                        </tr>
                     )}
                     <tr>
                        {type && (
                           <td>
                              <Icon
                                 name={thresholdIconName}
                                 size="12"
                                 color={IconColor.HONOLULU_BLUE}
                              />
                           </td>
                        )}
                        <td>
                           <Icon
                              name={getConfigurationIconName(field)}
                              size="12"
                              color={IconColor.HONOLULU_BLUE}
                           />
                        </td>
                        {oldValue !== null && !isBoolean(oldValue) && (
                           <>
                              {field === "cardColor" ? (
                                 <td>
                                    <CustomChangeItem
                                       className={classNames(
                                          "log-card__alert-card-color",
                                          {
                                             [`log-card__alert-card-color${getAlertVariantClassName(
                                                (
                                                   oldValue as string
                                                )?.toLowerCase() as keyof typeof AlertVariant
                                             )}`]: oldValue,
                                          }
                                       )}
                                    />
                                 </td>
                              ) : field === "typefaceColor" ? (
                                 <td>
                                    <CustomChangeItem
                                       className={classNames(
                                          "log-card__alert-card-typeface",
                                          {
                                             [`log-card__alert-card-typeface--${
                                                TypefaceVariant[
                                                   (
                                                      oldValue as string
                                                   ).toLowerCase() as keyof typeof TypefaceVariant
                                                ]
                                             }`]: oldValue,
                                          }
                                       )}
                                    >
                                       T
                                    </CustomChangeItem>
                                 </td>
                              ) : (
                                 <td className="log-card__change-old-value">
                                    {
                                       oldValue as
                                          string | number | (string | number)[]
                                    }
                                 </td>
                              )}
                              <td className="log-card__change-arrow">{` > `}</td>
                           </>
                        )}
                        {field === "cardColor" ? (
                           <td>
                              <CustomChangeItem
                                 className={classNames(
                                    "log-card__alert-card-color",
                                    {
                                       [`log-card__alert-card-color${getAlertVariantClassName(
                                          (
                                             newValue as string
                                          )?.toLowerCase() as keyof typeof AlertVariant
                                       )}`]: newValue,
                                    }
                                 )}
                              />
                           </td>
                        ) : field === "typefaceColor" ? (
                           <td>
                              <CustomChangeItem
                                 className={classNames(
                                    "log-card__alert-card-typeface",
                                    {
                                       [`log-card__alert-card-typeface--${
                                          TypefaceVariant[
                                             (
                                                newValue as string
                                             ).toLowerCase() as keyof typeof TypefaceVariant
                                          ]
                                       }`]: newValue,
                                    }
                                 )}
                              >
                                 T
                              </CustomChangeItem>
                           </td>
                        ) : (
                           <td>
                              {
                                 newValue as
                                    string | number | (string | number)[]
                              }
                           </td>
                        )}
                     </tr>
                  </Fragment>
               )
            })}
         </tbody>
      </table>
   )
}

export default ChangesTable
